import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CashJournalService } from '../service/cash-journal.service';
import { NotificationService } from '../service/notification.service';
import { WebSocketService } from '../service/websocket.service';

export interface WorkbenchF8 {
  id: number;
  time: string;
  amount: number;
  origin: string;
  isAvoir: boolean;       // Avoir
  groupId: string | null; // null = free
  suggestionTag?: string; // e.g: '💡 Match #1'
  isExactMatch?: boolean;
}

export interface WizardOrderLine {
  id: string; // generated ex: line-orderID-timestamp
  originalOrderId: number;
  productName: string;
  unitPrice: number;
  quantity: number;
  totalPrice: number;
  unit?: string; // e.g: 'P', 'KG', 'M'
  originalOrderDiscount?: number; // Total order-level reduction
  appliedReduction: number; // User-selected reduction for this line
  isSplit: boolean;
  groupId: string | null;
  suggestionTag?: string;
  time: string;
  isExactMatch?: boolean;
}

export interface WizardGroup {
  id: string;
  f8s: WorkbenchF8[];
  orderLines: WizardOrderLine[];
  totalF8: number;
  totalLines: number;
  discrepancy: number; // totalF8 - totalLines
}

@Component({
  selector: 'app-reconciliation-workbench',
  templateUrl: './reconciliation-workbench.component.html',
  styleUrls: ['./reconciliation-workbench.component.css']
})
export class ReconciliationWorkbenchComponent implements OnInit {

  date: string = new Date().toISOString().split('T')[0];
  isLoading = false;
  Math = Math; // Exposed for template

  // Available remaining items
  freeF8s: WorkbenchF8[] = [];
  freeOrderLines: WizardOrderLine[] = [];

  // Working groups
  groups: WizardGroup[] = [];

  // Drag state
  draggedF8: WorkbenchF8 | null = null;
  draggedOrderLine: WizardOrderLine | null = null;
  dragOverGroupId: string | null = null;

  // Process Modal State
  isProcessModalOpen = false;
  productSearch = '';

  // Draft save state
  draftSavedAt: string | null = null;
  get hasDraft(): boolean { return !!this.draftSavedAt; }

  // Split Modal State
  isSplitModalOpen = false;
  splitTargetLine: WizardOrderLine | null = null;
  splitQtyInput: number = 1;      // Used for qty-based split (stocked products)
  splitAmountInput: number = 0;   // Used for amount-based split (non-stocked products)
  isSplitByAmount = false;        // true = split by DH amount; false = split by quantity
  processSummary: {
    validGroups: WizardGroup[],
    incompleteGroups: WizardGroup[],
    missingF8Lines: WizardOrderLine[],
    excessF8s: WorkbenchF8[]
  } = { validGroups: [], incompleteGroups: [], missingF8Lines: [], excessF8s: [] };

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private cashJournalService: CashJournalService,
    private notificationService: NotificationService,
    private webSocketService: WebSocketService
  ) {}

  ngOnInit(): void {
    this.route.queryParams.subscribe(params => {
      if (params['date']) {
        this.date = params['date'];
      }
      this.loadData();
    });

    // Subscribing to real-time updates
    this.webSocketService.subscribeToTopic('/topic/reconciliation', (msg) => {
      console.log('📡 [WORKBENCH] Synchronization signal received:', msg);
      this.silentRefresh();
    });
  }

  isSyncing = false;
  private syncDebounceTimer: any = null;
  
  silentRefresh(): void {
    // Debounce: coalesce rapid WebSocket signals into a single fetch
    // This ensures we always get the LATEST data after all DB writes are committed
    if (this.syncDebounceTimer) {
      clearTimeout(this.syncDebounceTimer);
    }
    
    this.syncDebounceTimer = setTimeout(() => {
      if (this.isSyncing) return;
      this.isSyncing = true;
      
      this.cashJournalService.getReconciliationReport(this.date).subscribe({
        next: (report: any) => {
          this.mergeIncomingData(report);
          this.isSyncing = false;
        },
        error: () => this.isSyncing = false
      });
    }, 600); // Wait 600ms after last signal to ensure transaction is committed
  }

  loadData(): void {
    this.isLoading = true;
    this.cashJournalService.getReconciliationReport(this.date).subscribe({
      next: (report: any) => {
        this.buildWorkbenchFromReport(report);
        this.restoreDraft(); // Restore saved folder arrangement if available
        this.isLoading = false;
      },
      error: () => {
        this.notificationService.error('Erreur lors du chargement des données.');
        this.isLoading = false;
      }
    });
  }

  resetWorkbench(): void {
    if (confirm("🚨 RÉINITIALISATION : Êtes-vous sûr de vouloir annuler tout votre travail en cours sur cette journée ?")) {
      this.clearDraft();
      this.loadData();
      this.notificationService.success("Espace de travail réinitialisé.");
    }
  }

  onDateChange(newDate: string): void {
    this.date = newDate;
    // Mettre à jour l'URL sans recharger la page
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { date: this.date },
      queryParamsHandling: 'merge'
    });
    this.loadData();
  }

  private buildWorkbenchFromReport(report: any): void {
    this.freeF8s = [];
    this.freeOrderLines = [];
    this.groups = [];

    const processedF8Ids = new Set<number>();
    const processedOrderLinesSet = new Set<string>(); // Keep track to prevent duplicates
    let matchCounter = 1;

    if (report.suggestions) {
      for (const s of report.suggestions) {
        // Ignorer uniquement ce qui est DÉJÀ LIÉ en base de données.
        // On conserve les Matchs Exacts trouvés par l'algorithme, car ils ne sont pas encore confirmés.
        if (s.explanation && s.explanation.indexOf('Déjà réconcilié') !== -1) {
          continue;
        }

        // Si le système a trouvé un match exact non confirmé, on crée un tag visuel
        const isExact = s.matchStatus === 'EXACT';
        const tag = isExact ? `💡 MATCH #${matchCounter++}` : undefined;

        // 1. Extract F8s from detailed payload
        if (s.f8EntriesDetails && Array.isArray(s.f8EntriesDetails) && s.f8EntriesDetails.length > 0) {
          s.f8EntriesDetails.forEach((f8Detail: any) => {
            if (!processedF8Ids.has(f8Detail.id)) {
              processedF8Ids.add(f8Detail.id);
              const amt = f8Detail.amount || 0;
              const origin = f8Detail.origin || 'F8';
              const isAvoir = origin === 'AVOIR' || amt < 0;

              this.freeF8s.push({
                id: f8Detail.id,
                time: f8Detail.time || '--:--',
                amount: Math.abs(amt),
                origin,
                isAvoir,
                groupId: null,
                suggestionTag: tag,
                isExactMatch: isExact
              });
            }
          });
        } else {
          // Fallback legacy extraction
          const f8Ids: number[] = s.suggestedCashJournalIds || (s.cashJournalId ? [s.cashJournalId] : []);
          const tsArray = s.suggestedF8Timestamps || [s.f8Timestamp || '--:--'];

          f8Ids.forEach((id, index) => {
            if (!processedF8Ids.has(id)) {
              processedF8Ids.add(id);
              let amt = f8Ids.length === 1 ? s.f8Amount : (s.f8Amount / f8Ids.length);
              const isAvoir = s.origin === 'AVOIR' || (amt != null && amt < 0);
              this.freeF8s.push({
                id,
                time: tsArray[index] || tsArray[0] || '--:--',
                amount: Math.abs(amt || 0),
                origin: s.origin || 'F8',
                isAvoir,
                groupId: null,
                suggestionTag: tag,
                isExactMatch: isExact
              });
            }
          });
        }

        // 2. Extract Order Lines
        // The backend has injected `orderLines` array in the suggestion.
        if (s.orderLines && Array.isArray(s.orderLines)) {
          for (const line of s.orderLines) {
            // Unify identifier to avoid double insertion if a line appears in multiple suggestions
            const uniqueId = `ol-${line.originalOrderId}-${line.productName?.replace(/\s/g, '') || 'na'}-${line.unitPrice}`;
            if (!processedOrderLinesSet.has(uniqueId)) {
              processedOrderLinesSet.add(uniqueId);
              
              if (line.quantity > 0) {
                 this.freeOrderLines.push({
                  id: uniqueId + '-' + Date.now() + Math.floor(Math.random() * 1000), // Ensure unique per split
                  originalOrderId: line.originalOrderId,
                  productName: line.productName,
                  unitPrice: line.unitPrice,
                  quantity: line.quantity,
                  totalPrice: line.totalPrice,
                  unit: line.unit || 'P', // Mapping de l'unité (défaut à P si absent)
                  originalOrderDiscount: line.originalOrderDiscount || 0,
                  appliedReduction: 0,
                  isSplit: false,
                  groupId: null,
                  suggestionTag: tag,
                  time: line.time || '--:--',
                  isExactMatch: isExact
                });
              }
            }
          }
        }
      }
    }
    
    // Trier les F8 par heure (chronologique : du plus ancien au plus récent)
    this.freeF8s.sort((a, b) => a.time.localeCompare(b.time));
    
    // Trier les Produits par heure (chronologique : du plus ancien au plus récent)
    this.freeOrderLines.sort((a, b) => a.time.localeCompare(b.time));
    
    // Fix estimated amounts for F8s by fetching exact data if available (optional enhancement)
    // For now, if the user sees estimated amounts they can still drag them. We assume 99% of time it's 1-to-N.
  }

  private mergeIncomingData(report: any): void {
    if (!report) return;

    const suggestions = report.suggestions || [];
    
    // Helper: full baseId includes price (used for exact matching & dedup)
    const getBaseId = (id: string) => id.split('-').slice(0, 4).join('-'); 
    // Helper: STABLE key = orderId + productName only (ignores price/qty changes)
    // This ensures that if a price or quantity is modified, the line STAYS in its folder
    const getStableId = (orderId: number | string, productName: string) =>
      `sol-${orderId}-${(productName || 'na').replace(/\s/g, '')}`;

    // 1. Build COMPLETE maps of ALL items that should exist on the workbench
    const incomingF8Map = new Map<number, any>();
    const incomingLinesMap = new Map<string, any>();    // keyed by full baseId
    const incomingLinesByStableId = new Map<string, any>(); // keyed by stable id (no price)
    
    for (const s of suggestions) {
      if (s.explanation && s.explanation.indexOf('Déjà réconcilié') !== -1) continue;
      
      const tag = s.matchStatus === 'EXACT' ? s.matchStatus : null;

      // Extract F8s — prefer f8EntriesDetails, fallback to suggestedCashJournalIds + f8Amount
      if (s.f8EntriesDetails && Array.isArray(s.f8EntriesDetails) && s.f8EntriesDetails.length > 0) {
        for (const f8 of s.f8EntriesDetails) {
          if (!incomingF8Map.has(f8.id)) {
            incomingF8Map.set(f8.id, { ...f8, tag });
          }
        }
      } else {
        const f8Ids: number[] = s.suggestedCashJournalIds || (s.cashJournalId ? [s.cashJournalId] : []);
        const tsArray = s.suggestedF8Timestamps || [s.f8Timestamp || '--:--'];
        for (let i = 0; i < f8Ids.length; i++) {
          if (!incomingF8Map.has(f8Ids[i])) {
            incomingF8Map.set(f8Ids[i], {
              id: f8Ids[i],
              amount: f8Ids.length === 1 ? s.f8Amount : (s.f8Amount / f8Ids.length),
              time: tsArray[i] || tsArray[0] || '--:--',
              origin: s.origin || 'F8',
              tag
            });
          }
        }
      }
      
      // Extract order lines — build BOTH maps
      if (s.orderLines && Array.isArray(s.orderLines)) {
        for (const line of s.orderLines) {
          const bId = `ol-${line.originalOrderId}-${line.productName?.replace(/\s/g, '') || 'na'}-${line.unitPrice}`;
          const sId = getStableId(line.originalOrderId, line.productName);
          if (!incomingLinesMap.has(bId)) {
            incomingLinesMap.set(bId, { ...line, tag });
          }
          // The stable map stores the latest version of the line (for in-place updates)
          if (!incomingLinesByStableId.has(sId)) {
            incomingLinesByStableId.set(sId, { ...line, tag });
          }
        }
      }
    }

    let itemsRemoved = false;
    let itemsUpdated = false;

    // 2. PROCESS DELETIONS & UPDATES for FREE items
    this.freeF8s = this.freeF8s.filter(f => {
      const incoming = incomingF8Map.get(f.id);
      if (!incoming) { itemsRemoved = true; return false; }
      const newAmt = Math.abs(incoming.amount || 0);
      const newTime = incoming.time || '--:--';
      if (f.amount !== newAmt || f.time !== newTime) {
         f.amount = newAmt;
         f.time = newTime;
         itemsUpdated = true;
      }
      return true;
    });

    // For free order lines: try exact baseId first, then stable key
    this.freeOrderLines = this.freeOrderLines.filter(l => {
      const baseId = getBaseId(l.id);
      let incoming = incomingLinesMap.get(baseId);
      
      if (!incoming) {
        // Fallback: match by stable id (orderId + productName) — handles price changes
        const sId = getStableId(l.originalOrderId, l.productName);
        incoming = incomingLinesByStableId.get(sId);
      }
      
      if (!incoming) { itemsRemoved = true; return false; }
      
      // Update values in-place (only if not a split line)
      if (!l.isSplit) {
        if (l.quantity !== incoming.quantity || l.totalPrice !== incoming.totalPrice || l.unitPrice !== incoming.unitPrice) {
          l.quantity = incoming.quantity;
          l.unitPrice = incoming.unitPrice;
          l.totalPrice = incoming.totalPrice;
          l.productName = incoming.productName;
          // Rebuild the id to reflect new baseId
          const newBase = `ol-${incoming.originalOrderId}-${incoming.productName?.replace(/\s/g, '') || 'na'}-${incoming.unitPrice}`;
          l.id = newBase + '-' + Date.now() + Math.floor(Math.random() * 1000);
          itemsUpdated = true;
        }
      }
      return true;
    });

    // 3. PROCESS DELETIONS & UPDATES in GROUPS (folders)
    // KEY FIX: Use stable key (orderId + productName) to keep lines in folders
    // even when their price/quantity changes.
    this.groups.forEach(g => {
      const initialF8Count = g.f8s.length;
      const initialLineCount = g.orderLines.length;
      let groupNeedsRecalc = false;

      g.f8s = g.f8s.filter((f: WorkbenchF8) => {
        if (!incomingF8Map.has(f.id)) {
          this.notificationService.warning(`L'élément F8 (${f.time}) a été supprimé. Retiré du dossier.`);
          itemsRemoved = true;
          return false;
        }
        // Update F8 amount in-place if changed
        const inc = incomingF8Map.get(f.id);
        const newAmt = Math.abs(inc.amount || 0);
        if (f.amount !== newAmt) {
          f.amount = newAmt;
          groupNeedsRecalc = true;
          itemsUpdated = true;
        }
        return true;
      });

      g.orderLines = g.orderLines.filter((l: WizardOrderLine) => {
        const baseId = getBaseId(l.id);
        let incoming = incomingLinesMap.get(baseId);
        
        if (!incoming) {
          // Try stable key — this is the CORE FIX for modified order lines staying in folder
          const sId = getStableId(l.originalOrderId, l.productName);
          incoming = incomingLinesByStableId.get(sId);
        }

        if (!incoming) {
          // Line truly no longer exists (deleted or already reconciled)
          this.notificationService.warning(`L'article "${l.productName}" a été supprimé. Retiré du dossier.`);
          itemsRemoved = true;
          return false;
        }

        // Update line data in-place (quantity, price, etc.)
        if (!l.isSplit) {
          const changed = l.quantity !== incoming.quantity 
            || l.unitPrice !== incoming.unitPrice 
            || l.totalPrice !== incoming.totalPrice;
          if (changed) {
            l.quantity = incoming.quantity;
            l.unitPrice = incoming.unitPrice;
            l.totalPrice = incoming.totalPrice;
            l.productName = incoming.productName;
            // Update the id to reflect the new attributes
            const newBase = `ol-${incoming.originalOrderId}-${incoming.productName?.replace(/\s/g, '') || 'na'}-${incoming.unitPrice}`;
            l.id = newBase + '-upd-' + Date.now();
            groupNeedsRecalc = true;
            itemsUpdated = true;
          }
        }
        return true; // Line stays in folder ✅
      });

      if (g.f8s.length !== initialF8Count || g.orderLines.length !== initialLineCount || groupNeedsRecalc) {
        this.calculateGroupTotals(g);
      }
    });

    // 4. ADD NEW ITEMS (Additions)
    const existingF8Ids = new Set<number>();
    this.freeF8s.forEach(f => existingF8Ids.add(f.id));
    this.groups.forEach(g => g.f8s.forEach(f => existingF8Ids.add(f.id)));

    const existingLineStableIds = new Set<string>();
    this.freeOrderLines.forEach(l => existingLineStableIds.add(getStableId(l.originalOrderId, l.productName)));
    this.groups.forEach(g => g.orderLines.forEach((l: WizardOrderLine) => 
      existingLineStableIds.add(getStableId(l.originalOrderId, l.productName))));

    let addedCount = 0;
    let matchCounter = this.groups.length + 1;

    for (const s of suggestions) {
       if (s.explanation && s.explanation.indexOf('Déjà réconcilié') !== -1) continue;
       const isExact = s.matchStatus === 'EXACT';
       const tag = isExact ? `💡 MATCH #${matchCounter++}` : undefined;

       // New F8s — from f8EntriesDetails or fallback
       const f8Details: any[] = (s.f8EntriesDetails && s.f8EntriesDetails.length > 0)
         ? s.f8EntriesDetails
         : (s.suggestedCashJournalIds || (s.cashJournalId ? [s.cashJournalId] : [])).map((id: number, i: number) => ({
             id,
             amount: (s.suggestedCashJournalIds?.length || 1) === 1 ? s.f8Amount : (s.f8Amount / (s.suggestedCashJournalIds?.length || 1)),
             time: (s.suggestedF8Timestamps || [s.f8Timestamp || '--:--'])[i] || s.f8Timestamp || '--:--',
             origin: s.origin || 'F8'
           }));

       for (const f8 of f8Details) {
         if (f8.id != null && !existingF8Ids.has(f8.id)) {
           existingF8Ids.add(f8.id);
           const amt = f8.amount || 0;
           this.freeF8s.push({
             id: f8.id,
             time: f8.time || '--:--',
             amount: Math.abs(amt),
             origin: f8.origin || 'F8',
             isAvoir: f8.origin === 'AVOIR' || amt < 0,
             groupId: null,
             suggestionTag: tag,
             isExactMatch: isExact
           });
           addedCount++;
         }
       }

       // New Lines — only add if not already in freeOrderLines or a group (by stable id)
       if (s.orderLines) {
         for (const line of s.orderLines) {
            const sId = getStableId(line.originalOrderId, line.productName);
            if (!existingLineStableIds.has(sId)) {
              existingLineStableIds.add(sId);
              const baseId = `ol-${line.originalOrderId}-${line.productName?.replace(/\s/g, '') || 'na'}-${line.unitPrice}`;
              this.freeOrderLines.push({
                id: baseId + '-' + Date.now() + Math.floor(Math.random() * 1000),
                originalOrderId: line.originalOrderId,
                productName: line.productName,
                unitPrice: line.unitPrice,
                quantity: line.quantity,
                totalPrice: line.totalPrice,
                unit: line.unit || 'P',
                originalOrderDiscount: line.originalOrderDiscount || 0,
                appliedReduction: 0,
                isSplit: false,
                groupId: null,
                suggestionTag: tag,
                time: line.time || '--:--',
                isExactMatch: isExact
              });
              addedCount++;
            }
         }
       }
    }

    if (itemsRemoved || itemsUpdated || addedCount > 0) {
      this.freeF8s.sort((a, b) => a.time.localeCompare(b.time));
      this.freeOrderLines.sort((a, b) => a.time.localeCompare(b.time));
      
      let msg = "";
      if (addedCount > 0) msg += `${addedCount} nouveau(x) élément(s). `;
      if (itemsUpdated) msg += "Données mises à jour. ";
      if (itemsRemoved) msg += "Retraits détectés. ";
      
      this.notificationService.info(`🔄 Sync : ${msg}`);
    }
  }

  calculateGroupTotals(group: WizardGroup): void {
    group.totalF8 = group.f8s.reduce((sum, f) => sum + f.amount, 0);
    group.totalLines = group.orderLines.reduce((sum, l) => sum + l.totalPrice, 0);
    group.discrepancy = group.totalF8 - group.totalLines;
  }

  // ─────────────────────────────────────────────
  // ORDER LINE SPLITTING LOGIC
  // ─────────────────────────────────────────────

  // ─────────────────────────────────────────────
  // ORDER LINE SPLITTING (SMART DETACH)
  // ─────────────────────────────────────────────
  
  /** Returns true if the line is a non-stocked product (unit = 'NA') */
  isNonStockedLine(line: WizardOrderLine | null): boolean {
    if (!line) return false;
    return (line.unit || '').toUpperCase() === 'NA';
  }

  openSplitModal(line: WizardOrderLine): void {
    const isNonStocked = this.isNonStockedLine(line);
    
    if (!isNonStocked && line.quantity <= 1 && line.unit === 'P') {
      this.notificationService.warning("Impossible de diviser une unité atomique (Pièce).");
      return;
    }
    
    this.splitTargetLine = line;
    this.isSplitByAmount = isNonStocked;
    
    if (isNonStocked) {
      // DH mode: default to half the total price
      this.splitAmountInput = Math.round((line.totalPrice / 2) * 100) / 100;
    } else {
      // Qty mode: default to 1 piece or half
      this.splitQtyInput = line.unit === 'P' ? 1 : Math.min(1, line.quantity / 2);
    }
    this.isSplitModalOpen = true;
  }

  closeSplitModal(): void {
    this.isSplitModalOpen = false;
    this.splitTargetLine = null;
    this.isSplitByAmount = false;
  }

  getSplitRemainingQty(): number {
    if (!this.splitTargetLine) return 0;
    if (this.isSplitByAmount) return this.splitTargetLine.quantity; // qty unchanged in DH mode
    return Math.max(0, this.splitTargetLine.quantity - this.splitQtyInput);
  }

  getSplitAmountValue(): number {
    if (!this.splitTargetLine) return 0;
    if (this.isSplitByAmount) return this.splitAmountInput;
    return this.splitQtyInput * this.splitTargetLine.unitPrice;
  }

  getSplitRemainingValue(): number {
    if (!this.splitTargetLine) return 0;
    if (this.isSplitByAmount) return Math.max(0, this.splitTargetLine.totalPrice - this.splitAmountInput);
    return this.getSplitRemainingQty() * this.splitTargetLine.unitPrice;
  }

  /** Progress percentage for the balance bar */
  getSplitProgressPct(): number {
    if (!this.splitTargetLine) return 0;
    if (this.isSplitByAmount) {
      return Math.min(100, (this.splitAmountInput / (this.splitTargetLine.totalPrice || 1)) * 100);
    }
    return Math.min(100, (this.splitQtyInput / (this.splitTargetLine.quantity || 1)) * 100);
  }

  confirmSplit(): void {
    if (!this.splitTargetLine) return;
    const line = this.splitTargetLine;

    if (this.isSplitByAmount) {
      // ─── DH AMOUNT MODE (non-stocked products) ───
      if (this.splitAmountInput <= 0) {
        this.notificationService.error("Le montant à détacher doit être supérieur à 0.");
        return;
      }
      if (this.splitAmountInput >= line.totalPrice) {
        this.notificationService.error("Le montant détaché doit être inférieur au total de la ligne.");
        return;
      }

      const detachedAmount = Math.round(this.splitAmountInput * 100) / 100;
      const remainingAmount = Math.round((line.totalPrice - detachedAmount) * 100) / 100;

      // Update original line
      line.totalPrice = remainingAmount;
      line.unitPrice = remainingAmount; // For non-stocked: unitPrice = total (qty=1)
      line.isSplit = true;

      // Create detached line
      const newLine: WizardOrderLine = {
        ...line,
        id: line.id.split('-split-')[0] + '-split-dh-' + Date.now() + Math.random(),
        quantity: 1,
        unitPrice: detachedAmount,
        totalPrice: detachedAmount,
        isSplit: true,
        groupId: null
      };

      this.freeOrderLines.push(newLine);
      this.freeOrderLines.sort((a, b) => (a.time || '').localeCompare(b.time || ''));
      this.notificationService.success(`Détaché : ${detachedAmount.toFixed(2)} DH de "${line.productName}".`);

    } else {
      // ─── QTY MODE (stocked products) ───
      if (this.splitQtyInput <= 0) {
        this.notificationService.error("La quantité doit être supérieure à 0.");
        return;
      }
      if (this.splitQtyInput >= line.quantity) {
        this.notificationService.error("La quantité à détacher doit être inférieure à la quantité totale.");
        return;
      }
      if (line.unit === 'P' && !Number.isInteger(this.splitQtyInput)) {
        this.notificationService.error("Pour les pièces (P), utilisez des nombres entiers.");
        return;
      }

      const splitQty = this.splitQtyInput;
      line.quantity -= splitQty;
      line.totalPrice = line.quantity * line.unitPrice;
      line.isSplit = true;

      const newLine: WizardOrderLine = {
        ...line,
        id: line.id.split('-split-')[0] + '-split-' + Date.now() + Math.random(),
        unit: line.unit,
        quantity: splitQty,
        totalPrice: line.unitPrice * splitQty,
        isSplit: true,
        groupId: null
      };

      this.freeOrderLines.push(newLine);
      this.freeOrderLines.sort((a, b) => (a.time || '').localeCompare(b.time || ''));
      this.notificationService.success(`Détaché : ${splitQty}${line.unit || ''} de ${line.productName}.`);
    }

    this.closeSplitModal();
    this.saveWorkbenchDraft(); // Auto-save after split
  }

  // Old simple split kept for internal reference or deleted if not used
  splitOrderLine(line: WizardOrderLine): void {
    this.openSplitModal(line);
  }

  // ─────────────────────────────────────────────
  // GROUPS MANAGEMENT (Drag & Drop)
  // ─────────────────────────────────────────────

  createGroup(): WizardGroup {
    const group: WizardGroup = {
      id: 'grp-' + Date.now(),
      f8s: [],
      orderLines: [],
      totalF8: 0,
      totalLines: 0,
      discrepancy: 0
    };
    this.groups.unshift(group); // Add to top!
    return group;
  }

  addToGroup(itemType: 'f8' | 'line', item: any, groupId: string): void {
    if (item.groupId) return; // Already in a group
    
    const group = this.groups.find(g => g.id === groupId);
    if (!group) return;

    if (itemType === 'f8') {
      if (item.isAvoir) return; // Ignore Avoirs
      item.groupId = groupId;
      group.f8s.push(item);
    } else if (itemType === 'line') {
      item.groupId = groupId;
      group.orderLines.push(item);
    }

    this.recalcGroup(group);
    this.saveWorkbenchDraft(); // Auto-save after every change
  }

  removeFromGroup(itemType: 'f8' | 'line', item: any): void {
    const group = this.groups.find(g => g.id === item.groupId);
    if (!group) return;

    if (itemType === 'f8') {
      group.f8s = group.f8s.filter(f => f.id !== item.id);
    } else if (itemType === 'line') {
      group.orderLines = group.orderLines.filter(o => o.id !== item.id);
    }

    item.groupId = null;
    this.recalcGroup(group);

    // Auto-delete empty groups
    if (group.f8s.length === 0 && group.orderLines.length === 0) {
      this.groups = this.groups.filter(g => g.id !== group.id);
    }
    this.saveWorkbenchDraft(); // Auto-save after every change
  }

  deleteGroup(group: WizardGroup): void {
    for (const f8 of group.f8s) f8.groupId = null;
    for (const line of group.orderLines) line.groupId = null;
    this.groups = this.groups.filter(g => g.id !== group.id);
    this.saveWorkbenchDraft(); // Auto-save after every change
  }

  recalcGroup(group: WizardGroup): void {
    group.totalF8 = group.f8s.reduce((sum, f) => sum + f.amount, 0);
    group.totalLines = group.orderLines.reduce((sum, l) => sum + (l.totalPrice - (l.appliedReduction || 0)), 0);
    group.discrepancy = group.totalF8 - group.totalLines;
  }

  // ─────────────────────────────────────────────
  // DRAFT SAVE / RESTORE  (localStorage)
  // ─────────────────────────────────────────────

  private getDraftKey(): string {
    return `workbench-draft-${this.date}`;
  }

  /** Sauvegarder l'arrangement actuel des dossiers dans le localStorage */
  saveWorkbenchDraft(): void {
    const draft = {
      savedAt: new Date().toISOString(),
      groups: this.groups.map(g => ({
        id: g.id,
        // F8s: save by DB id (stable)
        f8Ids: g.f8s.map(f => f.id),
        // Order lines: save stable key (orderId + productName) + full object for split state
        orderLines: g.orderLines.map(l => ({
          stableKey: `${l.originalOrderId}|${(l.productName || '').trim()}`,
          quantity: l.quantity,
          unitPrice: l.unitPrice,
          totalPrice: l.totalPrice,
          appliedReduction: l.appliedReduction || 0,
          isSplit: l.isSplit || false,
          originalOrderId: l.originalOrderId,
          productName: l.productName
        }))
      }))
    };
    localStorage.setItem(this.getDraftKey(), JSON.stringify(draft));
    this.draftSavedAt = draft.savedAt;
  }

  /** Restaurer l'arrangement des dossiers depuis le localStorage après un chargement de données */
  private restoreDraft(): void {
    const raw = localStorage.getItem(this.getDraftKey());
    if (!raw) return;

    let draft: any;
    try { draft = JSON.parse(raw); } catch { return; }

    if (!draft?.groups?.length) return;

    // Helper: build stable key from a live line
    const lineKey = (l: WizardOrderLine) =>
      `${l.originalOrderId}|${(l.productName || '').trim()}`;

    let restored = 0;

    for (const savedGroup of draft.groups) {
      const f8sToAdd: WorkbenchF8[] = [];
      const linesToAdd: WizardOrderLine[] = [];

      // ── F8s: match by DB id (always stable) ──
      for (const f8Id of (savedGroup.f8Ids || [])) {
        const idx = this.freeF8s.findIndex(f => f.id === f8Id && !f.groupId);
        if (idx !== -1) f8sToAdd.push(this.freeF8s[idx]);
      }

      // ── Order lines: match by stable key (orderId + productName) ──
      for (const saved of (savedGroup.orderLines || [])) {
        const key = saved.stableKey
          || `${saved.originalOrderId}|${(saved.productName || '').trim()}`; // backward compat

        const idx = this.freeOrderLines.findIndex(
          l => lineKey(l) === key && !l.groupId
        );

        if (idx !== -1) {
          const freshLine = this.freeOrderLines[idx];

          // Restore split state: apply saved quantities to fresh line
          if (saved.isSplit) {
            freshLine.quantity = saved.quantity;
            freshLine.unitPrice = saved.unitPrice;
            freshLine.totalPrice = saved.totalPrice;
            freshLine.isSplit = true;
          }
          if (saved.appliedReduction > 0) {
            freshLine.appliedReduction = saved.appliedReduction;
          }

          linesToAdd.push(freshLine);
        }
      }

      if (f8sToAdd.length === 0 && linesToAdd.length === 0) continue;

      // Create the group
      const group: WizardGroup = {
        id: savedGroup.id || ('grp-restored-' + Date.now()),
        f8s: [],
        orderLines: [],
        totalF8: 0,
        totalLines: 0,
        discrepancy: 0
      };
      this.groups.push(group);

      for (const f8 of f8sToAdd) {
        f8.groupId = group.id;
        group.f8s.push(f8);
        restored++;
      }
      for (const line of linesToAdd) {
        line.groupId = group.id;
        group.orderLines.push(line);
        restored++;
      }
      this.recalcGroup(group);
    }

    if (restored > 0) {
      this.draftSavedAt = draft.savedAt;
      const savedDate = new Date(draft.savedAt);
      const displayTime = savedDate.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
      this.notificationService.success(`📂 Brouillon restauré (${displayTime}) — ${this.groups.length} dossier(s) récupéré(s).`);
    } else {
      // Draft exists but nothing matched (items reconciled in the meantime)
      localStorage.removeItem(this.getDraftKey());
      this.draftSavedAt = null;
    }
  }

  /** Effacer le brouillon sauvegardé pour cette date */
  clearDraft(): void {
    localStorage.removeItem(this.getDraftKey());
    this.draftSavedAt = null;
  }

  // ─────────────────────────────────────────────
  // DRAG & DROP HANDLERS
  // ─────────────────────────────────────────────

  onF8DragStart(f8: WorkbenchF8, event: DragEvent): void {
    this.draggedF8 = f8;
    this.draggedOrderLine = null;
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', 'f8');
    }
  }

  onLineDragStart(line: WizardOrderLine, event: DragEvent): void {
    this.draggedOrderLine = line;
    this.draggedF8 = null;
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', 'line');
    }
  }

  onDragEnd(): void {
    this.draggedF8 = null;
    this.draggedOrderLine = null;
    this.dragOverGroupId = null;
  }

  onDragOver(event: DragEvent, groupId?: string | null): void {
    event.preventDefault();
    this.dragOverGroupId = groupId || 'new';
  }

  onDrop(event: DragEvent, groupId?: string | null): void {
    event.preventDefault();
    if (!this.draggedF8 && !this.draggedOrderLine) return;

    let targetGroup: WizardGroup | undefined;
    
    if (groupId) {
      const index = this.groups.findIndex(g => g.id === groupId);
      if (index !== -1) {
        // Move existing group to top on interaction
        targetGroup = this.groups[index];
        if (index > 0) {
          this.groups.splice(index, 1);
          this.groups.unshift(targetGroup);
        }
      }
    }

    if (!targetGroup) {
      targetGroup = this.createGroup();
    }

    if (this.draggedF8) {
      this.addToGroup('f8', this.draggedF8, targetGroup.id);
    } else if (this.draggedOrderLine) {
      this.addToGroup('line', this.draggedOrderLine, targetGroup.id);
    }

    this.onDragEnd();
  }

  // ─────────────────────────────────────────────
  // PROCESS FINAL REPORT
  // ─────────────────────────────────────────────

  openProcessReport(): void {
    // 1. Reset
    this.processSummary = {
      validGroups: [],
      incompleteGroups: [],
      missingF8Lines: [],
      excessF8s: []
    };

    // 2. Analyze Groups
    for (const g of this.groups) {
      if (Math.abs(g.discrepancy) <= 0.05) {
        this.processSummary.validGroups.push(g);
      } else {
        this.processSummary.incompleteGroups.push(g);
      }
    }

    // 3. Analyze leftovers (Orphans)
    this.processSummary.missingF8Lines = this.freeOrderLines.filter(l => !l.groupId);
    this.processSummary.excessF8s = this.freeF8s.filter(f => !f.groupId && !f.isAvoir);

    this.isProcessModalOpen = true;
  }

  closeProcessReport(): void {
    this.isProcessModalOpen = false;
  }

  // ─────────────────────────────────────────────
  // UI HELPERS
  // ─────────────────────────────────────────────

  get availableF8s(): WorkbenchF8[] {
    // On garde tout (même ceux déjà réconciliés) pour ne pas casser l'ordre chronologique visuel
    return this.freeF8s.filter(f => !f.isAvoir);
  }

  get availableOrderLines(): WizardOrderLine[] {
    let lines = this.freeOrderLines.filter(l => !l.groupId);
    if (this.productSearch) {
      const normalize = (str: string) => 
        (str || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
      
      const searchBase = normalize(this.productSearch);
      const searchTerms = searchBase.split(' ').filter(t => t.trim().length > 0);

      lines = lines.filter(l => {
        const pName = normalize(l.productName);
        const orderId = l.originalOrderId?.toString() || '';
        
        // Match if all terms are found in either productName or orderId
        return searchTerms.every(term => 
          pName.includes(term) || orderId.includes(term)
        );
      });
    }
    return lines;
  }

  get globalF8Total(): number {
    // Somme DE TOUTE LA JOURNÉE (Pour rester stable pendant le travail)
    return this.freeF8s.reduce((sum, f) => sum + f.amount, 0);
  }

  get globalLinesTotal(): number {
    // Somme DE TOUTES LES VENTES CHARGÉES (Pour rester stable)
    return this.freeOrderLines.reduce((sum, l) => sum + l.totalPrice, 0);
  }

  isGroupValid(group: WizardGroup): boolean {
    return Math.abs(group.discrepancy || 0) <= 0.05;
  }

  calculateF8Total(group: WizardGroup): number {
    return group.totalF8 || 0;
  }

  calculateLinesTotal(group: WizardGroup): number {
    return group.totalLines || 0;
  }

  calculateDifference(group: WizardGroup): number {
    return (group.totalF8 || 0) - (group.totalLines || 0);
  }

  calculateTotalExcessF8(): number {
    if (!this.processSummary.excessF8s) return 0;
    return this.processSummary.excessF8s.reduce((sum, f) => sum + f.amount, 0);
  }



  formatAmount(val: number): string {
    return val.toFixed(2).replace('.', ',') + ' DH';
  }



  goBack(): void {
    this.router.navigate(['/reconciliation'], { queryParams: { date: this.date } });
  }
}
